package org.zq.astrock_ai.ui.research.gushi.components

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.TextUnit
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.zq.astrock_ai.theme.LocalAppFontFamily
import org.zq.astrock_ai.ui.research.gushi.StockColors
import org.zq.astrock_ai.ui.research.gushi.isPhoneWidth

@Composable
fun TopBar(
    modifier: Modifier = Modifier,
    onBack: (() -> Unit)? = null,
    onSearchCommit: ((String) -> Unit)? = null,
    onAiRecommend: (() -> Unit)? = null,
    onRecommendHistory: (() -> Unit)? = null,
    onGenerateLicenses: (() -> Unit)? = null,
    isLive: Boolean = false,
    loggedInPhone: String? = null,
    superAdmin: Boolean = false,
    staffRole: String = "user",
    licensePlanLabel: String = "",
    licenseExpiresLabel: String = "",
    onLogout: (() -> Unit)? = null,
) {
    var query by remember { mutableStateOf("") }
    val focusManager = LocalFocusManager.current
    val staffLabel = org.zq.astrock_ai.api.dto.StaffRoles.label(staffRole)

    BoxWithConstraints(modifier = modifier.background(Color.White)) {
        val phone = isPhoneWidth(maxWidth)
        val compact = maxWidth < 900.dp
        val canGenerate = onGenerateLicenses != null &&
            org.zq.astrock_ai.api.dto.StaffRoles.isSuper(staffRole, superAdmin)
        val hPad = when {
            phone -> 10.dp
            compact -> 12.dp
            else -> 20.dp
        }
        val licenseText = when {
            licenseExpiresLabel.isBlank() -> ""
            licensePlanLabel.isNotBlank() -> "会员至$licenseExpiresLabel（$licensePlanLabel）"
            else -> "会员至$licenseExpiresLabel"
        }

        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = hPad, vertical = if (phone) 8.dp else 10.dp),
        ) {
            if (phone) {
                PhoneTopBar(
                    onBack = onBack,
                    isLive = isLive,
                    licenseText = licenseText,
                    onLogout = onLogout,
                    onAiRecommend = onAiRecommend,
                    onRecommendHistory = onRecommendHistory,
                    onGenerateLicenses = if (canGenerate) onGenerateLicenses else null,
                    query = query,
                    onQueryChange = { query = it },
                    onSearchSubmit = {
                        focusManager.clearFocus()
                        onSearchCommit?.invoke(query.trim())
                    },
                )
            } else {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    if (onBack != null) {
                        Text(
                            "←",
                            fontSize = 20.sp,
                            color = StockColors.TextPrimary,
                            modifier = Modifier
                                .clip(RoundedCornerShape(8.dp))
                                .clickable(onClick = onBack)
                                .padding(horizontal = 6.dp, vertical = 4.dp),
                        )
                        Spacer(Modifier.width(4.dp))
                    }

                    BrandMark(fontSize = if (compact) 20.sp else 26.sp)

                    Spacer(Modifier.width(if (compact) 12.dp else 84.dp))
                    SearchBox(
                        value = query,
                        onValueChange = { query = it },
                        placeholder = if (compact) "搜索股票" else "搜索股票名称、代码、板块或概念",
                        modifier = Modifier
                            .weight(1f)
                            .widthIn(max = if (compact) 420.dp else 480.dp),
                        onSubmit = {
                            focusManager.clearFocus()
                            onSearchCommit?.invoke(query.trim())
                        },
                    )

                    if (onAiRecommend != null) {
                        Spacer(Modifier.width(8.dp))
                        TopActionChip(
                            label = "智启荐股",
                            onClick = onAiRecommend,
                            foreground = Color.White,
                            background = StockColors.PrimaryBlue,
                        )
                    }

                    if (onRecommendHistory != null) {
                        Spacer(Modifier.width(8.dp))
                        TopActionChip(
                            label = if (compact) "历史" else "推荐历史",
                            onClick = onRecommendHistory,
                            foreground = StockColors.PrimaryBlue,
                            background = Color(0xFFE8F0FE),
                        )
                    }

                    Spacer(Modifier.width(8.dp))
                    StatusPill(
                        label = if (isLive) "实时行情" else "演示数据",
                        live = isLive,
                    )
                    if (licenseText.isNotBlank()) {
                        Spacer(Modifier.width(8.dp))
                        Text(
                            licenseText,
                            color = StockColors.DownGreen,
                            fontSize = 12.sp,
                            fontWeight = FontWeight.SemiBold,
                            maxLines = 1,
                            softWrap = false,
                        )
                    }
                    if (staffLabel.isNotBlank()) {
                        Spacer(Modifier.width(8.dp))
                        Text(
                            staffLabel,
                            color = StockColors.UpRed,
                            fontSize = 12.sp,
                            fontWeight = FontWeight.SemiBold,
                            maxLines = 1,
                            softWrap = false,
                        )
                    }
                    if (canGenerate) {
                        Spacer(Modifier.width(8.dp))
                        TopActionChip(
                            label = "生成认证码",
                            onClick = onGenerateLicenses!!,
                            foreground = Color.White,
                            background = StockColors.UpRed,
                            bold = true,
                        )
                    }
                    if (onLogout != null) {
                        Spacer(Modifier.width(8.dp))
                        val masked = org.zq.astrock_ai.api.dto.PhoneNumbers.mask(loggedInPhone.orEmpty())
                        Text(
                            if (masked.isBlank()) "退出" else "$masked 退出",
                            color = StockColors.TextSecondary,
                            fontSize = 12.sp,
                            fontWeight = FontWeight.SemiBold,
                            maxLines = 1,
                            softWrap = false,
                            modifier = Modifier
                                .clip(RoundedCornerShape(999.dp))
                                .clickable(onClick = onLogout)
                                .padding(horizontal = 10.dp, vertical = 7.dp),
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun PhoneTopBar(
    onBack: (() -> Unit)?,
    isLive: Boolean,
    licenseText: String,
    onLogout: (() -> Unit)?,
    onAiRecommend: (() -> Unit)?,
    onRecommendHistory: (() -> Unit)?,
    onGenerateLicenses: (() -> Unit)?,
    query: String,
    onQueryChange: (String) -> Unit,
    onSearchSubmit: () -> Unit,
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        if (onBack != null) {
            Text(
                "←",
                fontSize = 18.sp,
                color = StockColors.TextPrimary,
                modifier = Modifier
                    .clip(RoundedCornerShape(8.dp))
                    .clickable(onClick = onBack)
                    .padding(end = 6.dp, top = 2.dp, bottom = 2.dp),
            )
        }
        BrandMark(fontSize = 17.sp)
        Spacer(Modifier.weight(1f))
        if (licenseText.isNotBlank()) {
            Text(
                licenseText,
                color = StockColors.DownGreen,
                fontSize = 11.sp,
                fontWeight = FontWeight.SemiBold,
                maxLines = 1,
                softWrap = false,
            )
            Spacer(Modifier.width(6.dp))
        }
        StatusPill(
            label = if (isLive) "实时" else "演示",
            live = isLive,
            compact = true,
        )
        if (onLogout != null) {
            Spacer(Modifier.width(4.dp))
            Text(
                "退出",
                color = StockColors.TextSecondary,
                fontSize = 12.sp,
                fontWeight = FontWeight.SemiBold,
                maxLines = 1,
                softWrap = false,
                modifier = Modifier
                    .clip(RoundedCornerShape(999.dp))
                    .clickable(onClick = onLogout)
                    .padding(horizontal = 8.dp, vertical = 6.dp),
            )
        }
    }

    Spacer(Modifier.height(8.dp))
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        if (onAiRecommend != null) {
            TopActionChip(
                label = "智启荐股",
                onClick = onAiRecommend,
                foreground = Color.White,
                background = StockColors.PrimaryBlue,
                modifier = Modifier.weight(1f),
                fill = true,
            )
        }
        if (onRecommendHistory != null) {
            TopActionChip(
                label = "历史",
                onClick = onRecommendHistory,
                foreground = StockColors.PrimaryBlue,
                background = Color(0xFFE8F0FE),
                modifier = Modifier.weight(1f),
                fill = true,
            )
        }
        if (onGenerateLicenses != null) {
            TopActionChip(
                label = "生成码",
                onClick = onGenerateLicenses,
                foreground = Color.White,
                background = StockColors.UpRed,
                modifier = Modifier.weight(1f),
                fill = true,
                bold = true,
            )
        }
    }

    Spacer(Modifier.height(8.dp))
    SearchBox(
        value = query,
        onValueChange = onQueryChange,
        placeholder = "搜索代码或名称",
        modifier = Modifier.fillMaxWidth(),
        compact = true,
        onSubmit = onSearchSubmit,
    )
}

@Composable
private fun BrandMark(fontSize: TextUnit) {
    Text(
        text = buildAnnotatedString {
            withStyle(SpanStyle(color = StockColors.UpRed, fontWeight = FontWeight.Bold)) {
                append("智启")
            }
            withStyle(SpanStyle(color = StockColors.TextPrimary, fontWeight = FontWeight.Bold)) {
                append("股势")
            }
        },
        fontSize = fontSize,
        maxLines = 1,
        softWrap = false,
    )
}

@Composable
private fun TopActionChip(
    label: String,
    onClick: () -> Unit,
    foreground: Color,
    background: Color,
    modifier: Modifier = Modifier,
    fill: Boolean = false,
    bold: Boolean = false,
) {
    Box(
        modifier = modifier
            .background(background, RoundedCornerShape(999.dp))
            .clip(RoundedCornerShape(999.dp))
            .clickable(onClick = onClick)
            .padding(horizontal = if (fill) 0.dp else 12.dp, vertical = 8.dp),
        contentAlignment = Alignment.Center,
    ) {
        Text(
            label,
            color = foreground,
            fontSize = 12.sp,
            fontWeight = if (bold) FontWeight.Bold else FontWeight.SemiBold,
            maxLines = 1,
            softWrap = false,
        )
    }
}

@Composable
private fun StatusPill(label: String, live: Boolean, compact: Boolean = false) {
    Row(
        modifier = Modifier
            .background(
                if (live) StockColors.UpSoft else Color(0xFFF0F2F5),
                RoundedCornerShape(999.dp),
            )
            .padding(horizontal = if (compact) 6.dp else 10.dp, vertical = if (compact) 4.dp else 6.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(if (compact) 4.dp else 6.dp),
    ) {
        Box(
            Modifier
                .size(7.dp)
                .clip(CircleShape)
                .background(if (live) StockColors.UpRed else StockColors.Neutral),
        )
        Text(
            label,
            color = if (live) StockColors.UpRed else StockColors.TextSecondary,
            fontSize = if (compact) 11.sp else 12.sp,
            fontWeight = FontWeight.SemiBold,
            maxLines = 1,
            softWrap = false,
            overflow = TextOverflow.Ellipsis,
        )
    }
}

@Composable
fun SearchBox(
    value: String,
    onValueChange: (String) -> Unit,
    placeholder: String,
    modifier: Modifier = Modifier,
    compact: Boolean = false,
    onSubmit: (() -> Unit)? = null,
) {
    BasicTextField(
        value = value,
        onValueChange = onValueChange,
        singleLine = true,
        textStyle = TextStyle(
            color = StockColors.TextPrimary,
            fontSize = if (compact) 13.sp else 14.sp,
            fontFamily = LocalAppFontFamily.current,
        ),
        keyboardOptions = KeyboardOptions(imeAction = ImeAction.Search),
        keyboardActions = KeyboardActions(onSearch = { onSubmit?.invoke() }),
        modifier = modifier,
        decorationBox = { inner ->
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(Color(0xFFF8F9FB), RoundedCornerShape(9.dp))
                    .padding(
                        horizontal = if (compact) 10.dp else 13.dp,
                        vertical = if (compact) 8.dp else 10.dp,
                    ),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(if (compact) 6.dp else 9.dp),
            ) {
                Text("⌕", color = StockColors.Neutral, fontSize = if (compact) 14.sp else 16.sp)
                Box(Modifier.weight(1f)) {
                    if (value.isBlank()) {
                        Text(
                            text = placeholder,
                            color = StockColors.Neutral,
                            fontSize = if (compact) 13.sp else 14.sp,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis,
                        )
                    }
                    inner()
                }
                if (value.isNotBlank() && onSubmit != null) {
                    Text(
                        "确定",
                        color = StockColors.PrimaryBlue,
                        fontWeight = FontWeight.Bold,
                        fontSize = 13.sp,
                        modifier = Modifier
                            .clip(RoundedCornerShape(6.dp))
                            .clickable(onClick = onSubmit)
                            .padding(horizontal = 6.dp, vertical = 2.dp),
                    )
                }
            }
        },
    )
}
